import type { ReactNode } from 'react';
import {
  CircleNotch as CircleNotchIcon,
  FileArrowUp as FileArrowUpIcon,
} from '@phosphor-icons/react';
import { Num, T } from 'gt-react';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import { WorkbookColumnPicker } from 'src/components/documents/WorkbookColumnPicker';
import { WorkbookSheetPicker } from 'src/components/documents/WorkbookSheetPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useBulkMappingUpload,
  type BulkUploadResultView,
  type MappingWorkbookPreview,
} from './useBulkMappingUpload';

function SummaryStat({
  label,
  value,
  destructive,
}: {
  label: ReactNode;
  value: number;
  destructive?: boolean;
}) {
  const className = destructive
    ? 'text-lg font-semibold text-destructive'
    : 'text-lg font-semibold';
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={className}>
        <Num>{value}</Num>
      </div>
    </div>
  );
}

function UploadSummary({
  result,
  partyColumnHeader,
}: {
  result: BulkUploadResultView;
  partyColumnHeader: ReactNode;
}) {
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <SummaryStat
          label={<T context="bulk mapping upload summary stat">Created</T>}
          value={result.createdCount}
        />
        <SummaryStat
          label={<T context="bulk mapping upload summary stat">Merged</T>}
          value={result.mergedCount}
        />
        <SummaryStat
          label={<T context="bulk mapping upload summary stat">Unmatched</T>}
          value={result.unmatchedCount}
          destructive={result.unmatchedCount > 0}
        />
        <SummaryStat
          label={<T context="bulk mapping upload summary stat">Skipped</T>}
          value={result.skippedRows}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="bulk mapping upload summary explanation">
          Created and merged counts are product mappings, not spreadsheet rows.
          Skipped rows had an empty part number in one of the picked columns.
        </T>
      </p>
      {result.unmatched.length > 0 ? (
        <UnmatchedRows result={result} partyColumnHeader={partyColumnHeader} />
      ) : null}
    </div>
  );
}

function UnmatchedRows({
  result,
  partyColumnHeader,
}: {
  result: BulkUploadResultView;
  partyColumnHeader: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">
        <T context="heading for rows whose internal part number matched no product">
          Unmatched internal part numbers
        </T>
      </div>
      <div className="max-h-64 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="unmatched rows table column">Row</T>
              </TableHead>
              <TableHead>{partyColumnHeader}</TableHead>
              <TableHead>
                <T context="unmatched rows table column">
                  Internal Part Number
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.unmatched.map((row) => (
              <TableRow key={row.rowIndex}>
                <TableCell>{row.rowIndex}</TableCell>
                <TableCell>{row.partyPart}</TableCell>
                <TableCell>{row.internalPart}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {result.unmatchedTruncated ? (
        <p className="text-xs text-muted-foreground">
          <T context="note that the unmatched rows list is capped">
            Showing the first <Num>{result.unmatched.length}</Num> of{' '}
            <Num>{result.unmatchedCount}</Num> unmatched rows.
          </T>
        </p>
      ) : null}
    </div>
  );
}

function SelectedFile({
  name,
  busy,
  onReplace,
}: {
  name: string;
  busy: boolean;
  onReplace: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <FileArrowUpIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate font-medium">{name}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto h-7"
        disabled={busy}
        onClick={onReplace}
      >
        <T context="replace the selected spreadsheet">Replace file</T>
      </Button>
    </div>
  );
}

function UploadProgress({ ingesting }: { ingesting: boolean }) {
  return (
    <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
      <CircleNotchIcon className="h-4 w-4 animate-spin" />
      {ingesting ? (
        <T context="bulk mapping upload progress">
          Matching part numbers and saving mappings…
        </T>
      ) : (
        <T context="bulk mapping upload progress">
          Uploading and reading the spreadsheet…
        </T>
      )}
    </div>
  );
}

function UploadFormBody<TPreview extends MappingWorkbookPreview>(props: {
  form: ReturnType<typeof useBulkMappingUpload<TPreview>>;
  partyLabel: ReactNode;
  partyColumnLabel: ReactNode;
  internalColumnLabel: ReactNode;
  partyColumnKey: string;
  internalColumnKey: string;
  renderPartyPicker: (
    partyId: string | undefined,
    onSelect: (id: string | undefined) => void,
    disabled: boolean,
  ) => ReactNode;
}) {
  const { form } = props;
  const preview = form.previewData;
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
      <div>
        <Label className="text-sm font-medium">{props.partyLabel}</Label>
        <div className="mt-1">
          {props.renderPartyPicker(form.partyId, form.setPartyId, form.busy)}
        </div>
      </div>
      <SpreadsheetField
        fileName={form.file?.name}
        busy={form.busy}
        onReplace={form.clearFile}
        onSelect={(file) => void form.selectFile(file)}
      />
      {preview && preview.sheetNames.length > 1 ? (
        <WorkbookSheetPicker
          sheetNames={preview.sheetNames}
          value={form.sheetName ?? preview.sheetName}
          onChange={(next) => void form.selectSheet(next)}
          disabled={form.busy}
        />
      ) : null}
      {preview ? (
        <WorkbookColumnPicker
          preview={preview}
          targets={[
            { key: props.partyColumnKey, label: props.partyColumnLabel },
            { key: props.internalColumnKey, label: props.internalColumnLabel },
          ]}
          value={form.columns}
          onChange={form.setColumn}
          disabled={form.busy}
        />
      ) : null}
      {form.busy ? <UploadProgress ingesting={form.ingestPending} /> : null}
    </div>
  );
}

function SpreadsheetField({
  fileName,
  busy,
  onReplace,
  onSelect,
}: {
  fileName?: string;
  busy: boolean;
  onReplace: () => void;
  onSelect: (file: File | null) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">
        <T context="bulk mapping upload spreadsheet section">Spreadsheet</T>{' '}
        <span className="font-normal text-muted-foreground">
          <T context="accepted spreadsheet file formats">(.xlsx, .xls, .csv)</T>
        </span>
      </div>
      {fileName ? (
        <SelectedFile name={fileName} busy={busy} onReplace={onReplace} />
      ) : (
        <FileUploadDropzone
          onFilesSelected={(files) => onSelect(files[0] ?? null)}
          uploadProgress={[]}
          isUploading={false}
          maxFiles={1}
          accept=".xlsx,.xls,.csv"
        />
      )}
    </div>
  );
}

export function ProductMatchBulkUploadModal<
  TPreview extends MappingWorkbookPreview,
>(props: {
  open: boolean;
  defaultPartyId?: string;
  onOpenChange: (open: boolean) => void;
  onUploaded: (partyId: string) => void;
  entityType: 'ar_customer_product' | 'ap_supplier_product';
  partyColumnKey: string;
  internalColumnKey: string;
  suggestedParty: (preview: TPreview) => number | null;
  suggestedInternal: (preview: TPreview) => number | null;
  previewPending: boolean;
  ingestPending: boolean;
  previewFile: (input: {
    documentId: string;
    sheetName?: string;
  }) => Promise<TPreview>;
  ingestFile: (input: {
    partyId: string;
    documentId: string;
    sheetName?: string;
    partyColumnIndex: number;
    internalColumnIndex: number;
  }) => Promise<BulkUploadResultView>;
  title: ReactNode;
  description: ReactNode;
  partyLabel: ReactNode;
  partyColumnLabel: ReactNode;
  partyColumnHeader: ReactNode;
  internalColumnLabel: ReactNode;
  readError: string;
  uploadError: string;
  sheetError: string;
  renderPartyPicker: (
    partyId: string | undefined,
    onSelect: (id: string | undefined) => void,
    disabled: boolean,
  ) => ReactNode;
}) {
  const form = useBulkMappingUpload({
    defaultPartyId: props.defaultPartyId,
    entityType: props.entityType,
    partyColumnKey: props.partyColumnKey,
    internalColumnKey: props.internalColumnKey,
    suggestedParty: props.suggestedParty,
    suggestedInternal: props.suggestedInternal,
    previewPending: props.previewPending,
    ingestPending: props.ingestPending,
    previewFile: props.previewFile,
    ingestFile: props.ingestFile,
    onUploaded: props.onUploaded,
    readError: props.readError,
    uploadError: props.uploadError,
    sheetError: props.sheetError,
  });

  const close = (next: boolean) => {
    if (!form.closeAllowed(next)) return;
    props.onOpenChange(next);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={close}
      disableBackdropClickClose={form.busy}
      disableEscapeKeyClose={form.busy}
    >
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-[46rem]">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{props.title}</DialogTitle>
          <DialogDescription>{props.description}</DialogDescription>
        </DialogHeader>
        {form.result ? (
          <UploadSummary
            result={form.result}
            partyColumnHeader={props.partyColumnHeader}
          />
        ) : (
          <UploadFormBody
            form={form}
            partyLabel={props.partyLabel}
            partyColumnLabel={props.partyColumnLabel}
            internalColumnLabel={props.internalColumnLabel}
            partyColumnKey={props.partyColumnKey}
            internalColumnKey={props.internalColumnKey}
            renderPartyPicker={props.renderPartyPicker}
          />
        )}
        <UploadFooter
          done={form.result !== null}
          busy={form.busy}
          canSubmit={form.canSubmit}
          ingesting={form.ingestPending}
          onClose={() => close(false)}
          onSubmit={() => void form.submit()}
        />
      </DialogContent>
    </Dialog>
  );
}

function UploadFooter({
  done,
  busy,
  canSubmit,
  ingesting,
  onClose,
  onSubmit,
}: {
  done: boolean;
  busy: boolean;
  canSubmit: boolean;
  ingesting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (done) {
    return (
      <DialogFooter className="shrink-0 border-t px-6 py-4">
        <Button onClick={onClose}>
          <T context="close the upload summary">Done</T>
        </Button>
      </DialogFooter>
    );
  }
  return (
    <DialogFooter className="shrink-0 border-t px-6 py-4">
      <Button variant="ghost" disabled={busy} onClick={onClose}>
        <T context="cancel bulk mapping upload">Cancel</T>
      </Button>
      <Button disabled={!canSubmit} onClick={onSubmit}>
        {ingesting ? (
          <CircleNotchIcon className="mr-1 h-4 w-4 animate-spin" />
        ) : null}
        <T context="submit bulk mapping upload">Upload Mappings</T>
      </Button>
    </DialogFooter>
  );
}
